import React, { useState, useEffect, useCallback, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { s3Link } from '../../utils/constants';
import { useHistory } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PostCategoryList from '../PostCategoryList';
import useDidMountEffect from '../../utils/useDidMountEffect';
import { connect, useDispatch } from 'react-redux';
import { setPostAction, setPostMedias, setSelectedPost, setSelectedPostGallery } from '../../../redux/actions';
import SilverLockIcon from '../../images/silver_lock.png';
import CloseButton from '../CloseButton';
import PostItem from "./post-item";
import store from '../../../redux/reducers/reducer';
import { inFreeTiers, postTypeNeedsSubscription } from '../../utils/helper';
import PThankYou from '../PThankYou';
import { setPostThankyou } from '../../../redux/actions/post';

const MediaContents = ({
    refetch = false,
    setRefetch = () => ({}),
    isPu = false,
    activePost,
    postMedias,
    action,
    showCreditCardForm,
    thankyou,
}) => {
    const dispatch = useDispatch();
    const perPage = 15;
    const userInformation = JSON.parse(localStorage.getItem('user'));
    const history = useHistory();
    const [show, setShow] = useState(false);
    const [post, setPost] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [medias, setMedias] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeMedia, setActiveMedia] = useState(post.medias ?? []);
    const [hasMore, setHasMore] = useState(false);
    const [activeCategory, setActiveCategory] = useState({});
    const [pu, setPu] = useState({});

    const observer = useRef();
    const cancelTokenSource = useRef();

    const lastMediaElementRef = useCallback(node => {
        if (isLoading) return;
        if (observer.current) {
            observer.current.disconnect();
        }

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setCurrentPage(currentPage => currentPage + 1);
            }
        });

        if (node) observer.current.observe(node);

        return () => {
            observer.current.disconnect();
        };
    }, [isLoading, hasMore]);

    const handleShow = (post) => {
        axios.get(`/api/posts/${post.id}/details`)
            .then((response) => {
                const pu = response.data.post.user;
                const post = response.data.post;
                setPost(post);
                setPu(pu);
                dispatch(setSelectedPostGallery(post));
                dispatch(setPostAction(''));
                setShow(true);
            })
            .catch((error) => {
                console.error('error', error);
            });
    };


    const updateActivePost = (post) => {
        axios.get(`/api/posts/${post.id}/details`)
            .then((response) => {
                const pu = response.data.post.user;
                const post = response.data.post;
                setPost(post);
                setPu(pu);
                dispatch(setSelectedPost(post));
            })
            .catch((error) => {
                console.error('error', error);
            });
    };

    const handleHide = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(setSelectedPost({}));
    };

    useEffect(() => {
        if (Object.keys(activeMedia).length > 0) {
            setShow(true);
        }
    }, [activeMedia]);


    useDidMountEffect(() => {
        let mounted = true;

        if (Object.keys(activePost).length === 0) {
            getMedias('onload');
            setShow(false);
        }

        if (post.unlocked === false && activePost.unlocked === true) {
            updateActivePost(activePost);
        }

        return (() => {
            mounted = false;
        });

    }, [activePost]);

    const pathname = window.location.pathname.replace('/', '');

    const getMedias = async (source = 'onload', ids = null) => {
        const cancelToken = axios.CancelToken;
        const tokenSource = cancelToken.source();
        cancelTokenSource.current = tokenSource;

        setIsLoading(true);

        let params = {
            user: pathname ?? userInformation.login,
        };

        if (!ids) {
            params = {
                ...params,
                page: source == 'onload' ? 1 : currentPage,
                per_page: perPage,
                is_pu: isPu,
                category_id: activeCategory && Object.keys(activeCategory).length > 0 ? activeCategory.id : '',
            };
        } else {
            params = {
                ...params,
                ids: ids
            };
        }


        await axios.get('/api/posts/medias', {
            params: params,
            cancelToken: tokenSource.token
        }).then(response => {
            const responseData = response.data.data;
            if (!ids || ids.length == 0) {
                setHasMore(responseData.length > 0);
                if (!refetch && source == 'onload') {
                    setMedias(responseData);
                    dispatch(setPostMedias(responseData));
                } else {
                    const mediaIds = new Set(medias.map(post => post.id));
                    const merged = [...medias, ...responseData.filter(d => !mediaIds.has(d.id))];
                    setMedias(merged);
                    dispatch(setPostMedias(merged));
                }
            } else {
                let tmp = [...postMedias];

                responseData.forEach(post => {
                    const index = tmp.findIndex(t => t.id == post.id);
                    if (index >= 0) {
                        tmp[index] = post;
                    }
                });
                dispatch(setPostMedias([...tmp]));
                setMedias([...tmp]);
            }
            setRefetch(false);
        }).catch(error => {
            console.error({ error });
        }).finally(() => {
            setIsLoading(false);
        });
    };

    useEffect(() => {
        let source = 'onload';
        if (currentPage > 1) {
            source = 'loadMore';
        }

        getMedias(source);
    }, [currentPage]);

    const handlePostData = e => {
        const postMediasStore = store.getState().post.postMedias;
        const newPost = e.detail.post;

        let tmp = [...postMediasStore];
        const index = tmp.findIndex(t => t.id == newPost.id);

        if (index >= 0) {
            tmp[index] = newPost;
            setMedias([...tmp]);
            dispatch(setPostMedias([...tmp]));
        }
    };

    const handleMediaUpdate = e => {
        updateActivePost(e.detail.post);

        // fetch the rest of post medias
        const requireSubscriptionPostIds = postMedias
            .filter(p => postTypeNeedsSubscription(p.post_type))
            .map(p => p.id);
        getMedias('', requireSubscriptionPostIds);
    };

    useEffect(() => {
        const handleUnlocked = (e) => {
            handlePostData(e);
        };

        const handleUpdate = e => handleMediaUpdate(e);

        window.addEventListener('post/unlocked', handleUnlocked);
        window.addEventListener('media-contents/subscription', handleUpdate);

        return () => {
            dispatch(setSelectedPostGallery({}));

            window.removeEventListener('post/unlocked', handleUnlocked);
            window.removeEventListener('media-contents/subscription', handleUpdate);

            if (observer.current) {
                observer.current.disconnect();
            }

            if (cancelTokenSource.current) {
                cancelTokenSource.current.cancel('Cancel shop request');
            }
        };
    }, []);

    const handleClose = () => {
        setShow(false);
        dispatch(setPostAction(''));
        dispatch(setSelectedPostGallery({}));
    };

    useEffect(() => {
        if (action === "edit") {
            setShow(false);
        }
    }, [action]);

    useDidMountEffect(() => {
        getMedias('onload');
    }, [activeCategory]);

    const handleLike = (index) => {
        const currentLike = !posts[index].is_liked_by_user;
        posts[index].is_liked_by_user = currentLike;

        if (currentLike === false) {
            const userIndex = posts[index].likes.findIndex(like => like.user_id == userInformation.id);
            posts[index].likes.splice(userIndex, 1);
        } else {
            posts[index].likes.push({ user_id: userInformation.id });
        }

        setPosts(
            [...posts],
            posts[index]
        );
    };

    useEffect(() => {
        const handleUnlockedPost = (e) => {
            setPost(e.detail.post);
        };

        if (show) {
            window.addEventListener('post/unlocked', handleUnlockedPost);
        } else {
            window.removeEventListener('post/unlocked', handleUnlockedPost);
        }

        return () => {
            window.removeEventListener('post/unlocked', handleUnlockedPost);
        };
    }, [show]);

    useEffect(() => {
        $('#pu-content-container').height('fit-content');
    }, [medias]);

    const handleThankyou = (payload) => {
        dispatch(setPostThankyou({ ...thankyou, show: payload }));
    };

    return (
        <>
            {/* <div className="media-contents-container mb-4"> */}
            <div className="media-contents-container">
                <PostCategoryList
                    activeCategory={activeCategory}
                    setActiveCategory={setActiveCategory}
                    setCurrentPage={setCurrentPage}
                    viewType="media"
                />

                <div className={`media-items-container${medias.length === 0 ? 'd-block' : ''}`}>
                    {
                        medias.map((post, index) => {
                            if (medias.length === index + 1) {
                                return <div
                                    ref={lastMediaElementRef}
                                    className={`
                                        media-item
                                        ${!post.unlocked && post.medias.length > 1 && !post.has_free_preview
                                            ? ' media-item__bundle'
                                            : ''
                                        }`
                                    }
                                    key={post.id}
                                >
                                    {
                                        post.medias.length > 1
                                            ? <div className="media-item__icon">
                                                <FontAwesomeIcon icon={['far', 'images']} />
                                            </div>
                                            : null
                                    }
                                    {
                                        post.medias.length == 1 && post.medias[0].type == 'VIDEO'
                                            ? <div className="media-item__icon">
                                                <FontAwesomeIcon icon={['fas', 'video']} />
                                            </div>
                                            : null
                                    }

                                    <LazyLoadImage
                                        src={
                                            post.unlocked ? `${s3Link}/${post.medias[0].thumb_path}`
                                                : (
                                                    post.medias_count > 1 // bundle
                                                        ? (
                                                            post.has_free_preview
                                                                && !postTypeNeedsSubscription(post.post_type)
                                                                && !inFreeTiers(post.follow_status)
                                                                ? `${s3Link}/${post.medias[0].thumb_path}`
                                                                : `${SilverLockIcon}`
                                                        )
                                                        : `${s3Link}/${post.medias[0].blurred_path}`
                                                )
                                        }
                                        effect="blur"
                                        onClick={() => handleShow(post)}
                                        className={`
                                            ${!post.unlocked && post.medias.length > 1 && !post.has_free_preview
                                                ? 'img-gallery__bundle'
                                                : 'img-fluid'
                                            }
                                        `}
                                        onContextMenu={e => e.preventDefault()}
                                        nosave="true"
                                    />
                                </div>;
                            } else {
                                return <div
                                    className={`
                                        media-item
                                        ${!post.unlocked && post.medias_count > 1 && !post.has_free_preview
                                            ? ' media-item__bundle'
                                            : ''
                                        }`
                                    }
                                    key={post.id}
                                    onClick={() => handleShow(post)}
                                >
                                    {
                                        post.medias_count > 1
                                            ? <div className="media-item__icon">
                                                <FontAwesomeIcon icon={['far', 'images']} />
                                            </div>
                                            : null
                                    }
                                    {
                                        post.medias_count == 1 && post.medias[0].type == 'VIDEO'
                                            ? <div className="media-item__icon">
                                                <FontAwesomeIcon icon={['fas', 'video']} />
                                            </div>
                                            : null
                                    }
                                    <LazyLoadImage
                                        src={
                                            post.unlocked ? `${s3Link}/${post.medias[0].thumb_path}`
                                                : (
                                                    post.medias_count > 1 // bundle
                                                        ? (
                                                            post.has_free_preview
                                                                ? `${s3Link}/${post.medias[0].thumb_path}`
                                                                : `${SilverLockIcon}`
                                                        )
                                                        : `${s3Link}/${post.medias[0].blurred_path}`
                                                )
                                        }
                                        effect="blur"
                                        // onClick={() => handleShow(post)}
                                        className={`
                                            ${!post.unlocked && post.medias_count > 1 && !post.has_free_preview
                                                ? 'img-gallery__bundle'
                                                : 'img-fluid'
                                            }
                                        `}
                                        onContextMenu={e => e.preventDefault()}
                                        nosave="true"
                                    />
                                </div>;
                            }
                        })
                    }

                    {
                        isLoading
                            ? <div className="my-4 text-center" style={{ width: '100%' }}>
                                <Spinner animation="border" role="status">
                                    <span className="sr-only">Loading...</span>
                                </Spinner>
                            </div>
                            : (
                                medias.length == 0
                                    ? <div className="my-4 text-center" style={{ width: '100%' }}>
                                        <span className="sr-only profile-no-data">No media posted</span>
                                    </div>
                                    : null
                            )
                    }

                </div>
            </div>
            {
                show && !showCreditCardForm
                    ? <Modal
                        show={show}
                        onHide={handleClose}
                        className="media-content-modal post-grid-modal"
                        backdrop="static"
                        animation={false}
                        keyboard={false}
                    >
                        <Modal.Body className={'post-gallery-container'}>
                            <CloseButton
                                onClick={handleClose}
                                style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 99 }}
                            />
                            <div className={'post-gallery-body'}>
                                {show && <PostItem
                                    post={post}
                                    pu={pu}
                                    key={post.id}
                                    likeHandler={handleLike}
                                    index={post.id}
                                    refetch={refetch}
                                    setRefetch={setRefetch}
                                    autoplay={false}
                                />}
                            </div>
                        </Modal.Body>
                    </Modal>
                    : null
            }

            {
                thankyou.show
                    ? <PThankYou
                        show={thankyou.show}
                        pu={thankyou.pu}
                        setShow={handleThankyou}
                        isUpgrade={thankyou.upgrade}
                    />
                    : null
            }
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        activePost: state.post.selectedPost,
        postMedias: state.post.postMedias,
        action: state.post.action,
        showCreditCardForm: state.privaGlobal.creditCardForm.show,
        thankyou: state.post.thankyou,
    };
};

export default React.memo(connect(mapStateToProps)(MediaContents));
